import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { catchError, expand, map, share, takeWhile, tap } from "rxjs/operators";
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, ReplaySubject, Subject, throwError } from "rxjs";

import { environment } from "src/environments/environment";
import { UserBasicProfile } from "src/app/shared/models/user-basic-profile.mode";
import { AuthService, AuthType } from "src/app/shared/services/auth.service";
import { SharedService } from "src/app/shared/shared.service";
import { IMember } from "src/app/groups/models/group-detail.model";
import { AlertService } from "../alert/alert.service";

enum ExceptionMessages {
  "USER_NOT_FOUND" = "User Not found.",
}
const EXCEED_IDENEDI_GROUP_ID_KEY = "exceed.user.idenedi.group.id";

export interface IpData {
  countryCode: string;
  callingCode: string;
}

export interface UserBasic {
  CountryISOCode: string;
  EntityType: string;
  FirstName: string;
  InstanceId: string;
  IsChildAccount: boolean;
  LastName: string;
  ProfileImageUrl: string;
}

@Injectable({
  providedIn: "root",
})
export class UserService {

  auth_token: string;
  reload_contacts = new Subject<boolean>();
  reload_notifications = new Subject<boolean>();
  reload_profileuserpanel = new Subject<boolean>();
  private memberData = new BehaviorSubject<any>(null);
  // Observable string streams
  memberData$ = this.memberData.asObservable();
  
  // Cache for getCurrentUser
  private currentUserCache: { data: UserBasicProfile; timestamp: number } | null = null;
  private readonly CACHE_DURATION_MS = 2 * 1000; // 2 seconds in milliseconds

  private readonly IP_DATA_STORAGE_KEY = "stemexe.user.ipdata";

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private sharedService: SharedService,
    private snackBar: MatSnackBar,
    private alertService: AlertService
  ) {
  }

  // Sending data of a specific member to manage-module-access
  sendMemberData(data: any) {
    this.memberData.next(data);
  }

  getCurrentUser(): Observable<UserBasicProfile> {
    if (!this.authService.isLoggedIn()) {
      let err:any = new Error('User not logged in.');
      return of(err);
    }
    // Check if we have cached data that's still valid
    if (this.currentUserCache && this.isCacheValid()) {
      return of(this.currentUserCache.data);
    }

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.get<any>(`${environment.apiHost}/api/user`, options).pipe(
      tap((userData) => {
        // Cache the successful response
        this.currentUserCache = {
          data: userData,
          timestamp: Date.now()
        };
      }),
      catchError((err) => {
        if (err.error.ExceptionMessage === ExceptionMessages.USER_NOT_FOUND) {
          this.authService.logout();
        }
        return of(err);
      })
    );
  }

  private isCacheValid(): boolean {
    if (!this.currentUserCache) {
      return false;
    }
    return (Date.now() - this.currentUserCache.timestamp) < this.CACHE_DURATION_MS;
  }

  getMembers(
    searchQuery: string = "*all*",
    skip: number = 0,
    limit: number = 20
  ): Observable<IMember[]> {
    const url = `${environment.apiHost}/api/group/${this.authService.idenediGroupId}/member`;
    const params = new HttpParams({
      fromObject: {
        query: searchQuery || "*all*",
        skip: skip + "",
        top: limit + "",
        memType: "Member",
      },
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params,
    };
    return this.http.get<IMember[]>(url, options);
  }
  getAllMembers(): Observable<IMember[]> {
    const url = `${environment.apiHost}/api/group/${this.authService.idenediGroupId}/member`;
    const params = new HttpParams({
      fromObject: {
        query: "*all*",
        memType: "Member",
        skip: "0",
        top: "10000000",
      },
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params,
    };
    return this.http.get<IMember[]>(url, options);
  }
  getUserData(userIds: string[]): Observable<UserBasic[]> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    if (!userIds || !userIds.length) {
      return of([]);
    }
    const chunkedArrays = this.chunkArray(userIds, 50);
    const chunkedRequests = chunkedArrays.map((arr) => {
      return this.http.get<UserBasic[]>(
        `${environment.apiHost}/api/user/basic?idenedis=${arr.join(",")}`,
        options
      );
    });
    return forkJoin(chunkedRequests).pipe(
      map((chunkedResponse: UserBasic[][]) => {
        return chunkedResponse.flat();
      })
    );
  }
  private chunkArray(arr: any[], n: number) {
    return arr.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / n);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);
  }
  getUserSocialPoints(
    memberId: string
  ): Observable<{ rank: number; score: number }> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    const body = {
      groupId: this.authService.idenediGroupId,
      memberId,
    };
    return this.http.post<{ rank: number; score: number }>(
      `${environment.idenediMlbackend}/api/ML/GetScoreByMember`,
      body,
      options
    );
  }

  getMockUser(): Observable<UserBasicProfile> {
    return this.http.get<UserBasicProfile>("assets/mock/user.json");
  }

  detectUserIpData(useJsonp = false): Observable<IpData> {
    const localRaw = localStorage.getItem(this.IP_DATA_STORAGE_KEY);
    if (localRaw) {
      return of(JSON.parse(localRaw) as IpData);
    }

    const sessionRaw = sessionStorage.getItem(this.IP_DATA_STORAGE_KEY);
    if (sessionRaw) {
      if (this.authService.isLoggedIn()) {
        localStorage.setItem(this.IP_DATA_STORAGE_KEY, sessionRaw);
        sessionStorage.removeItem(this.IP_DATA_STORAGE_KEY);
      }
      return of(JSON.parse(sessionRaw) as IpData);
    }

    const url = `${environment.ipDataServiceUrl}?api-key=${environment.ipDataApiKey}`;
    const httpRequestObs = useJsonp
      ? this.http.jsonp<any>(url, "callback")
      : this.http.get<any>(url);
    return httpRequestObs.pipe(
      map((response) => ({
        countryCode: response.country_code,
        callingCode: response.calling_code,
      })),
      tap((ipData) => {
        const serialized = JSON.stringify(ipData);
        if (this.authService.isLoggedIn()) {
          localStorage.setItem(this.IP_DATA_STORAGE_KEY, serialized);
        } else {
          sessionStorage.setItem(this.IP_DATA_STORAGE_KEY, serialized);
        }
      }),
      share()
    );
  }

  promoteIpDataToLocalStorage(): void {
    const sessionRaw = sessionStorage.getItem(this.IP_DATA_STORAGE_KEY);
    if (sessionRaw) {
      localStorage.setItem(this.IP_DATA_STORAGE_KEY, sessionRaw);
      sessionStorage.removeItem(this.IP_DATA_STORAGE_KEY);
    }
  }

  updateIpDataCache(ipData: IpData): void {
    localStorage.setItem(this.IP_DATA_STORAGE_KEY, JSON.stringify(ipData));
    sessionStorage.removeItem(this.IP_DATA_STORAGE_KEY);
  }

  setUserInfo(
    firstName: string,
    lastName: string,
    country: string
  ): Observable<any> {
    const body = {
      FirstName: firstName,
      LastName: lastName,
      CountryIso: country,
    };
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.post<any>(
      `${environment.idenediProviderUrl}/api/account/info`,
      body,
      options
    );
  }

  uploadProfilePhoto(file: File): Observable<{ str: string }> {
    const formData: FormData = new FormData();
    const isImgType = this.sharedService.isFileImage(file);
    if (isImgType && file) {
      formData.append("file", file);
      const options = {
        headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      };
      return this.http
        .post<{ str: string }>(
          `${environment.apiHost}/api/user/profileimage`,
          formData,
          options
        )
        .pipe(
          catchError((errorRes) => {
            this.alertService.error("ExdrCommon.ServerError");
            throw errorRes;
          }),
          tap(() => {
            this.snackBar.open("Photo updated successfully!", null, {
              duration: 2000,
              panelClass: ["success-snack-bar"],
            });
          })
        );
    }
  }

  sendVerificationEmail(email: string) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.post<any>(
      `${environment.apiHost}/api/user/email?email=${email}`,
      {},
      options
    );
  }

  getGroupMembers(
    groupIdenedi: string,
    skip?: number,
    top?: number,
    searchText?: string,
    memType?: string,
    orderBy?: string
  ): Observable<IMember[]> {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/member`;

    // Initialize HttpParams
    let params = new HttpParams();

    // Add parameters conditionally
    if (searchText) params = params.set('query', searchText);
    if (memType) params = params.set('memType', memType);
    if (skip !== undefined) params = params.set('skip', skip.toString());
    if (top !== undefined) params = params.set('top', top.toString());
    if (orderBy) params = params.set('orderBy', orderBy);
    params = params.set('_ts', Date.now().toString());

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params,
    };

    return this.http.get<IMember[]>(url, options);
  }


  setGroupAdmin(groupIdenedi: string, memberIdenedi: string) {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/member/${memberIdenedi}/admin`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.put<any>(url, {}, options);
  }


  removeGroupAdmin(groupIdenedi: string, memberIdenedi: string) {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/member/${memberIdenedi}/admin`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.delete<any>(url, options);
  }
  appAccessUpdate(appId: string, memberIdenedi: string, role: string) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    const url = `${environment.apiHost}/api/group/${this.authService.idenediGroupId}/member/${memberIdenedi}/app/${appId}/role`;
    const payload = [role];
    return this.http.put<any>(url, payload, options);
  }

  removeGroupMember(groupIdenedi: string, memberIdenedi: string) {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/member`;
    let params: HttpParams = new HttpParams({
      fromObject: {
        idenedi: memberIdenedi,
      },
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params,
    };
    return this.http.delete<any>(url, options);
  }
  addGroupMembers(groupIdenedi: string, memberIdenedis: string[]) {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/member`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.post<any>(url, memberIdenedis, options);
  }

  getUserDetail(idenedi: string): Observable<IMember> {
    if (idenedi != null) {
      localStorage.setItem(EXCEED_IDENEDI_GROUP_ID_KEY, idenedi);
    }
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.get<any>(
      `${environment.apiHost}/api/user/${idenedi}`,
      options
    );
  }

  setUserTitle(
    groupIdenedi: string,
    memberIdenedi: string,
    title: string
  ): Observable<any> {
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Content-Type", "application/json"),
    };
    return this.http.put<any>(
      `${environment.apiHost}/api/group/${groupIdenedi}/member/${memberIdenedi}/title`,
      `"${title}"`,
      options
    );
  }


  setUserName(
    fullName: any
  ): Observable<any> {
    // https://idenediapi-prod-stag.azurewebsites.net/api/user/setname
    const url = `${environment.apiHost}/api/user/setname`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.post<any>(url, fullName, options);
  }

  updateGroupMember(
    groupIdenedi: string,
    memberIdenedi: string,
    adminRole: string
  ): Observable<any> {
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Content-Type", "application/json"),
    };

    // ["contentCreate"] , null
    return this.http.put<any>(
      `${environment.apiHost}/api/group/${groupIdenedi}/member/${memberIdenedi}/adminrole`,
      adminRole === null ? `null` : ` ["${adminRole}"]`,
      options
    );
  }

  getUserInfoDetails(groupId: string, memberId: string) {
    // api/group/{groupIdenedi}/member/{memberIdenedi}
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.get<any>(
      `${environment.apiHost}/api/group/${groupId}/member/${memberId}`,
      options
    );
  }

  updateBioInfo(userBio: string): Observable<any> {
    const queryString = 'api/user/bio';
    const url = `${environment.apiHost}/${queryString}`;
    // Create the request body
    const body = JSON.stringify(userBio);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${this.authService.idenediToken}` });

    const options = {
      headers: headers
    };

    // Perform the HTTP PUT request and return an observable
    return this.http.put<any>(url, body, options).pipe(
      catchError((error) => {
        if (error.error.ExceptionMessage === ExceptionMessages.USER_NOT_FOUND) {
          this.authService.logout();
        }
        return throwError(() => error); // Rethrow the error for the subscriber
      })
    );
  }

  // To get user's contact information
  getUserRecords(): Observable<any> {
    this.auth_token = this.authService.idenediToken;
    const options = {
      headers: {
        Authorization: `Bearer ${this.auth_token}`,
      },
    };
    return this.http.get<any>(`${environment.apiHost}/api/record`, options);
  }

  //Update user's contact information record sequence
  updateRecords(order: any): Observable<any> {
    this.auth_token = this.authService.idenediToken;
    const options = {
      headers: {
        Authorization: `Bearer ${this.auth_token}`,
      },
    };
    return this.http.post<any>(`${environment.apiHost}/api/record/order`, order, options);
  }

  //Update user contact visibility
  updatecontactVisibility(order: any, groupId: string): Observable<any> {
    this.auth_token = this.authService.idenediToken;
    const options = {
      headers: {
        Authorization: `Bearer ${this.auth_token}`,
      },
    };
    return this.http.put<any>(`${environment.apiHost}/api/card/${groupId}/records`, order, options);
  }

  //Remove user contact info
  removeContact(instanceId: string): Observable<any> {
    this.auth_token = this.authService.idenediToken;
    const options = {
      headers: {
        Authorization: `Bearer ${this.auth_token}`,
      },
    };
    return this.http.delete<any>(`${environment.apiHost}/api/record?instanceIds[0]=${instanceId}`, options);
  }

  // Create new contact for user
  createContact(type: string, record: any): Observable<any> {
    this.auth_token = this.authService.idenediToken;
    const options = {
      headers: {
        Authorization: `Bearer ${this.auth_token}`,
      },
    };
    return this.http.post<any>(`${environment.apiHost}/api/record/${type}`, record, options)
  }

  //Update user contact info
  updateContactInfo(itemID: string, record: any): Observable<any> {
    this.auth_token = this.authService.idenediToken;
    const options = {
      headers: {
        Authorization: `Bearer ${this.auth_token}`,
      },
    };
    return this.http.put<any>(`${environment.apiHost}/api/record/${itemID}`, record, options);
  }

}
